import React, { useEffect, useState } from "react";
import { TrendingUp, Users, DollarSign, Award, Loader2, UserPlus } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, suffix, testid }) => (
  <div
    data-testid={testid}
    className="bg-white rounded-2xl p-5 border border-[#e0f5ef] shadow-sm"
  >
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 rounded-xl bg-[#16C79A]/10 flex items-center justify-center">
        <Icon size={18} className="text-[#16C79A]" />
      </div>
      <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{label}</p>
    </div>
    <p className="text-3xl font-extrabold text-[#0C2D48]">
      {value}
      {suffix && <span className="text-lg text-slate-400 font-medium ml-1">{suffix}</span>}
    </p>
  </div>
);

export const AdminAnalytics = ({ token, refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/admin/analytics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError("Could not load analytics.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, refreshKey]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-[#e0f5ef] p-12 flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-[#16C79A]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white rounded-2xl border border-[#e0f5ef] p-8 text-center">
        <p className="text-sm text-red-500">{error || "No analytics available."}</p>
      </div>
    );
  }

  const { totals, signups_per_day, top_referrers } = data;

  // Format chart labels — show short date label
  const chartData = signups_per_day.map((d) => ({
    date: d.date,
    label: new Date(d.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
    count: d.count,
  }));

  return (
    <div className="space-y-6" data-testid="admin-analytics-panel">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Sign-Ups"
          value={totals.total_signups}
          testid="stat-total-signups"
        />
        <StatCard
          icon={Award}
          label="Active Subs"
          value={totals.active_subscriptions}
          testid="stat-active-subs"
        />
        <StatCard
          icon={TrendingUp}
          label="MRR"
          value={totals.mrr_aed.toLocaleString()}
          suffix="AED"
          testid="stat-mrr"
        />
        <StatCard
          icon={DollarSign}
          label="Total Revenue"
          value={totals.total_revenue_aed.toLocaleString()}
          suffix="AED"
          testid="stat-total-revenue"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Signups per day chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#e0f5ef] p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm uppercase tracking-wider font-bold text-[#0C2D48]/60">Sign-Ups — Last 30 Days</h3>
              <p className="text-lg font-extrabold text-[#0C2D48] mt-1">
                {chartData.reduce((sum, d) => sum + d.count, 0)} total
              </p>
            </div>
          </div>
          <div className="h-64" data-testid="signups-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0f5ef" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  interval={Math.max(0, Math.floor(chartData.length / 8))}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: "#f0faf7" }}
                  contentStyle={{
                    background: "#ffffff",
                    border: "1px solid #e0f5ef",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                  labelStyle={{ color: "#0C2D48", fontWeight: 600 }}
                />
                <Bar dataKey="count" fill="#16C79A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top referrers */}
        <div className="bg-white rounded-2xl border border-[#e0f5ef] p-6 shadow-sm">
          <h3 className="text-sm uppercase tracking-wider font-bold text-[#0C2D48]/60 mb-4">Top Referrers</h3>
          {top_referrers.length === 0 ? (
            <div className="text-center py-8">
              <UserPlus size={28} className="text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No referrals yet.</p>
              <p className="text-xs text-slate-400 mt-1">Share your referral links to start tracking.</p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="top-referrers-list">
              {top_referrers.slice(0, 5).map((r, i) => (
                <li
                  key={r.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl hover:bg-[#f5fcf9] transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      i === 0 ? "bg-[#16C79A] text-white" : "bg-[#f0faf7] text-[#0C2D48]"
                    }`}>
                      {i + 1}
                    </div>
                    <span className="text-sm font-semibold text-[#0C2D48] truncate">{r.name}</span>
                  </div>
                  <span className="text-sm font-bold text-[#16C79A] flex-shrink-0">
                    {r.count} <span className="text-xs text-slate-400 font-medium">
                      {r.count === 1 ? "referral" : "referrals"}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
